import { useState, useMemo } from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  IconButton, 
  Box, 
  Typography, 
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { FileDownload as FileDownloadIcon, PieChart as PieChartIcon, BarChart as BarChartIcon } from '@mui/icons-material';
import { ResponsiveBar } from '@nivo/bar';
import { chartTheme } from './chartTheme';
import { fetchProductStatusMatrix } from '@/services/api';
import { PRIMARY_CHART_COLORS, getDistinctColors, getConsistentColor, DISTRIBUTION_CHART_COLORS } from './colorUtils';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

interface ProductData {
  name: string;
  value: number;
}

interface StatusData {
  name: string;
  value: number;
  products: Array<{ name: string; value: number }>;
}

export interface StackedBarChartProps {
  title: string;
  data: Array<StatusData>;
  onBarClick?: (status: string, product: string) => void;
  compact?: boolean;
  initialViewBy?: 'product' | 'status';
  viewBy?: 'product' | 'status';
  onViewChange?: (view: 'product' | 'status') => void;
}

// Helper function to truncate text
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

export const StackedBarChart = ({ 
  title, 
  data, 
  onBarClick, 
  compact = false, 
  initialViewBy = 'product',
  viewBy,
  onViewChange
}: StackedBarChartProps) => {
  const [tooltipData, setTooltipData] = useState<Record<string, { totalLeads: number; statusPercentage: number }>>({});
  const [loadingTooltip, setLoadingTooltip] = useState<string | null>(null);
  const [internalViewBy, setInternalViewBy] = useState<'product' | 'status'>(initialViewBy);
  
  // Use either controlled or uncontrolled view mode
  const currentViewBy = viewBy !== undefined ? viewBy : internalViewBy;

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: 'product' | 'status' | null) => {
    if (newView !== null) {
      if (onViewChange) {
        onViewChange(newView);
      } else {
        setInternalViewBy(newView);
      }
    }
  };

  const handleMouseEnter = async (bar: any) => {
    const status = bar.id;
    const product = bar.indexValue;
    const tooltipKey = `${status}-${product}`;
    
    if (!tooltipData[tooltipKey]) {
      setLoadingTooltip(tooltipKey);
      try {
        const result = await fetchProductStatusMatrix(status, product);
        const productMetrics = result.metrics[product];
        setTooltipData(prev => ({
          ...prev,
          [tooltipKey]: {
            totalLeads: productMetrics.total_leads,
            statusPercentage: productMetrics.status_percentage
          }
        }));
      } catch (error) {
        console.error('Error fetching tooltip data:', error);
      } finally {
        setLoadingTooltip(null);
      }
    }
  };

  const formatData = () => {
    if (currentViewBy === 'product') {
      const products = new Set<string>();
      data.forEach(status => {
        status.products.forEach(product => {
          products.add(product.name);
        });
      });

      return Array.from(products).map(product => {
        const row: { [key: string]: string | number } = { product };
        data.forEach(status => {
          row[status.name] = status.products.find(p => p.name === product)?.value || 0;
        });
        return row;
      });
    } else {
      // Group by status with products as keys
      return data.map(status => {
        const row: { [key: string]: string | number } = { status: status.name };
        status.products.forEach(product => {
          row[product.name] = product.value;
        });
        return row;
      });
    }
  };

  const getKeys = () => {
    if (currentViewBy === 'product') {
      return data.map(d => d.name);
    } else {
      const products = new Set<string>();
      data.forEach(status => {
        status.products.forEach(product => {
          products.add(product.name);
        });
      });
      return Array.from(products);
    }
  };

  // Generate distinct colors for status and products
  const statusColors = useMemo(() => {
    const statusList = data.map(d => d.name);
    return getDistinctColors(statusList.length);
  }, [data]);

  const productColors = useMemo(() => {
    const productSet = new Set<string>();
    data.forEach(status => {
      status.products.forEach(product => {
        productSet.add(product.name);
      });
    });
    return getDistinctColors(productSet.size);
  }, [data]);

  // Check if this is a distribution chart based on title
  const isDistributionChart = title?.toLowerCase().includes('status distribution') || 
                             title?.toLowerCase().includes('channel distribution') ||
                             title?.toLowerCase().includes('product distribution');

  // Get all status and product names for consistent color assignment
  const statusList = useMemo(() => data.map(d => d.name), [data]);
  
  const productList = useMemo(() => {
    const productSet = new Set<string>();
    data.forEach(status => {
      status.products.forEach(product => {
        productSet.add(product.name);
      });
    });
    return Array.from(productSet);
  }, [data]);

  // Get color for a specific status - use direct array access for no repetition
  const getStatusColor = (status: string): string => {
    if (isDistributionChart) {
      const index = statusList.indexOf(status);
      if (index >= 0 && index < DISTRIBUTION_CHART_COLORS.length) {
        return DISTRIBUTION_CHART_COLORS[index];
      }
      // Fallback
      return '#64B5F6';
    }
    
    // For non-distribution charts, use the original logic
    return getConsistentColor(status);
  };

  // Get color for a specific product - use direct array access for no repetition
  const getProductColor = (product: string): string => {
    if (isDistributionChart) {
      const index = productList.indexOf(product);
      if (index >= 0 && index < DISTRIBUTION_CHART_COLORS.length) {
        return DISTRIBUTION_CHART_COLORS[index];
      }
      // Fallback
      return '#64B5F6';
    }
    
    // For non-distribution charts, use the original logic
    return getConsistentColor(product);
  };

  // Calculate totals for each key for the legend
  const calculateTotals = () => {
    const keys = getKeys();
    const totals: Record<string, number> = {};
    
    if (currentViewBy === 'product') {
      // For product view, get total for each status
      data.forEach(status => {
        totals[status.name] = status.value;
      });
    } else {
      // For status view, calculate total for each product
      keys.forEach(product => {
        totals[product] = data.reduce((sum, status) => {
          const productData = status.products.find(p => p.name === product);
          return sum + (productData?.value || 0);
        }, 0);
      });
    }
    
    return totals;
  };

  const handleExport = () => {
    const csvContent = [
      ['Product', ...data.map(d => d.name)].join(','),
      ...formatData().map(row => [
        row.product,
        ...data.map(status => row[status.name])
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${title.toLowerCase().replace(/\s+/g, '_')}.csv`;
    link.click();
  };

  const formatTooltipContent = (id: string | number, indexValue: string | number, value: number) => {
    let color;
    
    if (currentViewBy === 'product') {
      // In product view, id is the status
      color = getStatusColor(String(id));
    } else {
      // In status view, id is the product
      color = getProductColor(String(id));
    }
    
    return (
      <>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '8px',
          gap: '8px'
        }}>
          <div style={{
            width: '12px',
            height: '12px',
            backgroundColor: color,
            borderRadius: '2px'
          }} />
          <strong style={{ color: color }}>
            {id}
          </strong>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px'
          }}>
            <span style={{ color: '#666' }}>Count:</span>
            <strong>{value.toLocaleString()}</strong>
          </div>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px'
          }}>
            <span style={{ color: '#666' }}>{currentViewBy === 'product' ? 'Product:' : 'Status:'}</span>
            <strong>{indexValue}</strong>
          </div>
        </div>
      </>
    );
  };

  return (
    <Card sx={{ 
      height: compact ? '100%' : 'auto',
      boxShadow: compact ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.08)',
      border: compact ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
      borderRadius: 2,
      overflow: 'visible'
    }}>
      {!compact && (
        <CardHeader 
          title={
            <Typography variant="h6" component="h2">
              {title}
            </Typography>
          }
          action={
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2 
            }}>
              <ToggleButtonGroup
                value={currentViewBy}
                exclusive
                onChange={handleViewChange}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 2,
                    py: 0.5,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#475569',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(54, 162, 235, 0.15)',
                      color: '#36A2EB',
                      '&:hover': {
                        backgroundColor: 'rgba(54, 162, 235, 0.25)',
                      },
                    },
                  },
                  '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
                    borderRadius: '4px 0 0 4px',
                  },
                  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                    borderRadius: '0 4px 4px 0',
                  },
                }}
              >
                <ToggleButton 
                  value="product"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  By Product
                </ToggleButton>
                <ToggleButton 
                  value="status"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  By Status
                </ToggleButton>
              </ToggleButtonGroup>
              <IconButton 
                onClick={handleExport} 
                title="Export data"
                sx={{ 
                  color: '#36A2EB',
                  '&:hover': {
                    backgroundColor: 'rgba(54, 162, 235, 0.1)'
                  }
                }}
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
          }
          sx={{
            '& .MuiCardHeader-action': {
              margin: 0,
              alignSelf: 'center',
            },
          }}
        />
      )}
      <CardContent sx={{ 
        height: compact ? '100%' : 450,
        p: compact ? 0 : undefined
      }}>
        <Box sx={{ height: '100%' }}>
          <ResponsiveBar
            data={formatData()}
            keys={getKeys()}
            indexBy={currentViewBy === 'product' ? 'product' : 'status'}
            margin={{ 
              top: compact ? 10 : 20, 
              right: compact ? 20 : 160, 
              bottom: compact ? 40 : 100, 
              left: compact ? 40 : 60 
            }}
            padding={compact ? 0.1 : 0.2}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={({ id }) => {
              if (currentViewBy === 'product') {
                // In product view, id is the status
                return getStatusColor(String(id));
              } else {
                // In status view, id is the product
                return getProductColor(String(id));
              }
            }}
            theme={{
              ...chartTheme,
              axis: {
                ...chartTheme.axis,
                ticks: {
                  ...chartTheme.axis.ticks,
                  text: {
                    fontSize: compact ? 9 : 11,
                    fill: '#2d3748',
                    textTransform: 'capitalize'
                  }
                }
              },
              legends: {
                text: {
                  fontSize: compact ? 9 : 12,
                  fill: '#2d3748',
                  textTransform: 'capitalize'
                }
              }
            }}
            borderRadius={4}
            borderWidth={0}
            enableGridY={false}
            enableGridX={false}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: compact ? 0 : 5,
              tickPadding: compact ? 5 : 10,
              tickRotation: -45,
              legend: compact ? undefined : (currentViewBy === 'product' ? '' : ''),
              legendPosition: 'middle',
              legendOffset: compact ? 30 : 70,
              format: (value) => truncateText(value, compact ? 8 : 15)
            }}
            axisLeft={{
              tickSize: compact ? 0 : 5,
              tickPadding: compact ? 3 : 5,
              tickRotation: 0,
              legend: compact ? undefined : 'Count',
              legendPosition: 'middle',
              legendOffset: compact ? -25 : -40,
              format: (value) => compact ? 
                (value >= 1000 ? `${Math.floor(value / 1000)}k` : value) : 
                value.toLocaleString(),
              tickValues: []
            }}
            enableLabel={!compact}
            labelSkipWidth={compact ? 20 : 12}
            labelSkipHeight={compact ? 20 : 12}
            labelTextColor="#000000"
            layers={[
              'grid',
              'axes',
              'bars',
              'markers',
              'legends',
              'annotations'
            ]}
            legends={compact ? [] : [
              {
                dataFrom: 'keys',
                anchor: 'bottom-right',
                direction: 'column',
                justify: false,
                translateX: 120,
                translateY: 0,
                itemsSpacing: 2,
                itemWidth: 100,
                itemHeight: 20,
                itemDirection: 'left-to-right',
                itemOpacity: 0.85,
                symbolSize: 20,
                symbolShape: 'square',
                itemTextColor: '#333333',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemOpacity: 1
                    }
                  }
                ],
                data: (() => {
                  const keys = getKeys();
                  const totals = calculateTotals();
                  
                  return keys.map(key => {
                    const color = currentViewBy === 'product' 
                      ? getStatusColor(key)
                      : getProductColor(key);
                      
                    return {
                      id: key,
                      label: `${key} (${totals[key].toLocaleString()})`,
                      color: color
                    };
                  });
                })()
              }
            ]}
            tooltip={({ id, value, indexValue, color }) => (
              <div style={{
                background: 'white',
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: compact ? '10px' : '12px'
              }}>
                {formatTooltipContent(id, indexValue, value)}
                {loadingTooltip === `${id}-${indexValue}` ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mt: 1,
                    py: 1,
                    borderTop: '1px solid rgba(54, 162, 235, 0.1)'
                  }}>
                    <CircularProgress 
                      size={compact ? 16 : 20} 
                      sx={{ color: '#36A2EB' }}
                    />
                  </Box>
                ) : tooltipData[`${id}-${indexValue}`] ? (
                  <div style={{ 
                    marginTop: '8px',
                    padding: '8px 0 0',
                    borderTop: '1px solid rgba(54, 162, 235, 0.1)'
                  }}>
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '4px'
                    }}>
                      <span style={{ color: '#666' }}>Total Base:</span>
                      <strong>{tooltipData[`${id}-${indexValue}`].totalLeads.toLocaleString()}</strong>
                    </div>
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: '#666' }}>Status %:</span>
                      <strong style={{ color }}>
                        {tooltipData[`${id}-${indexValue}`].statusPercentage.toFixed(1)}%
                      </strong>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            onClick={(data) => {
              if (currentViewBy === 'product') {
                onBarClick?.(String(data.id), String(data.indexValue));
              } else {
                onBarClick?.(String(data.indexValue), String(data.id));
              }
            }}
            animate={true}
            motionConfig="gentle"
            role="application"
            ariaLabel="Product Status Distribution"
          />
        </Box>
      </CardContent>
    </Card>
  );
}; 